import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { SEO } from "../components/SEO";
import {
  Package, Plus, Trash2, ShoppingCart, Shirt, Flag, Sticker,
  Minus, ArrowRight, Sparkles, Calendar, Users, Trophy, Heart,
  Building2, Music, GraduationCap, PartyPopper
} from "lucide-react";
import { GARMENT_TYPES } from "../constants";

/* ── Event Planner AI Suggestions ── */
const EVENT_TYPES = [
  { id: "5k_run", label: "5K / Fun Run", icon: Trophy, suggestions: [
    { label: "Race T-Shirts", garment: "tshirt", method: "screen_printing", qty: 100 },
    { label: "Volunteer Tanks", garment: "tank", method: "dtf_printing", qty: 20 },
    { label: "Finish Line Banner", type: "banner", qty: 2 },
    { label: "Bib Number Stickers", type: "stickers", qty: 200 },
  ]},
  { id: "wedding", label: "Wedding / Party", icon: Heart, suggestions: [
    { label: "Bridal Party Tees", garment: "tshirt", method: "dtf_printing", qty: 12 },
    { label: "Custom Stickers (Favors)", type: "stickers", qty: 100 },
    { label: "Welcome Banner", type: "banner", qty: 1 },
  ]},
  { id: "corporate", label: "Corporate Event", icon: Building2, suggestions: [
    { label: "Embroidered Polos", garment: "polo", method: "embroidery", qty: 50 },
    { label: "Executive Hoodies", garment: "hoodie", method: "screen_printing", qty: 25 },
    { label: "Branded Hats", garment: "hat", method: "embroidery", qty: 50 },
    { label: "Event Banner", type: "banner", qty: 3 },
  ]},
  { id: "school", label: "School / Graduation", icon: GraduationCap, suggestions: [
    { label: "Class T-Shirts", garment: "tshirt", method: "screen_printing", qty: 75 },
    { label: "Staff Polos", garment: "polo", method: "embroidery", qty: 15 },
    { label: "Graduation Banner", type: "banner", qty: 1 },
  ]},
  { id: "concert", label: "Concert / Festival", icon: Music, suggestions: [
    { label: "Band Merch Tees", garment: "tshirt", method: "dtf_printing", qty: 200 },
    { label: "VIP Hoodies", garment: "hoodie", method: "dtf_printing", qty: 30 },
    { label: "Festival Stickers", type: "stickers", qty: 500 },
    { label: "Stage Banners", type: "banner", qty: 4 },
  ]},
  { id: "fundraiser", label: "Fundraiser", icon: PartyPopper, suggestions: [
    { label: "Fundraiser Tees", garment: "tshirt", method: "screen_printing", qty: 50 },
    { label: "Supporter Stickers", type: "stickers", qty: 200 },
    { label: "Yard Signs", type: "banner", qty: 10 },
  ]},
  { id: "sports", label: "Sports Team", icon: Trophy, suggestions: [
    { label: "Team Jerseys", garment: "tshirt", method: "screen_printing", qty: 20 },
    { label: "Coach Polos", garment: "polo", method: "embroidery", qty: 5 },
    { label: "Team Hats", garment: "hat", method: "embroidery", qty: 25 },
  ]},
  { id: "tradeshow", label: "Trade Show", icon: Calendar, suggestions: [
    { label: "Staff Polos", garment: "polo", method: "embroidery", qty: 10 },
    { label: "Giveaway Tees", garment: "tshirt", method: "dtf_printing", qty: 100 },
    { label: "Booth Banners", type: "banner", qty: 3 },
    { label: "Promo Stickers", type: "stickers", qty: 500 },
  ]},
];

const PACKAGE_TEMPLATES = [
  {
    id: "team_event",
    name: "Team Event Package",
    desc: "T-Shirts + Hats for your team",
    items: [
      { garment: "tshirt", method: "dtf_printing", qty: 25, label: "Custom T-Shirts" },
      { garment: "hat", method: "embroidery", qty: 25, label: "Embroidered Hats" },
    ],
  },
  {
    id: "corporate",
    name: "Corporate Branding Kit",
    desc: "Polos + Hoodies + Banners",
    items: [
      { garment: "polo", method: "embroidery", qty: 20, label: "Embroidered Polos" },
      { garment: "hoodie", method: "screen_printing", qty: 15, label: "Screen Print Hoodies" },
      { type: "banner", label: "Vinyl Banner (3x6 ft)", qty: 2 },
    ],
  },
  {
    id: "fundraiser",
    name: "Fundraiser Starter",
    desc: "T-Shirts + Stickers",
    items: [
      { garment: "tshirt", method: "screen_printing", qty: 50, label: "Screen Print Tees" },
      { type: "stickers", label: "Custom Stickers (3x3)", qty: 200 },
    ],
  },
];

const PRODUCT_OPTIONS = [
  { id: "tshirt-dtf", garment: "tshirt", method: "dtf_printing", label: "T-Shirt — DTF Print", price: 24.03, icon: Shirt },
  { id: "tshirt-screen", garment: "tshirt", method: "screen_printing", label: "T-Shirt — Screen Print", price: 18.50, icon: Shirt },
  { id: "hoodie-dtf", garment: "hoodie", method: "dtf_printing", label: "Hoodie — DTF Print", price: 42.00, icon: Shirt },
  { id: "hoodie-screen", garment: "hoodie", method: "screen_printing", label: "Hoodie — Screen Print", price: 38.00, icon: Shirt },
  { id: "polo-emb", garment: "polo", method: "embroidery", label: "Polo — Embroidery", price: 22.00, icon: Shirt },
  { id: "hat-emb", garment: "hat", method: "embroidery", label: "Hat — Embroidery", price: 15.00, icon: Shirt },
  { id: "tank-dtf", garment: "tank", method: "dtf_printing", label: "Tank Top — DTF Print", price: 22.00, icon: Shirt },
  { id: "stickers", type: "stickers", label: "Custom Stickers", price: 0.35, icon: Sticker },
  { id: "banner", type: "banner", label: "Vinyl Banner", price: 3.50, icon: Flag },
];

const EventPackages = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [attendees, setAttendees] = useState("");

  const loadFromEvent = (eventType) => {
    setSelectedEvent(eventType.id);
    const count = parseInt(attendees) || 0;
    const multiplier = count > 0 ? count / (eventType.suggestions[0]?.qty || 50) : 1;
    const newItems = eventType.suggestions.map((s, i) => ({
      id: `evt-${Date.now()}-${i}`,
      ...s,
      qty: count > 0 ? Math.max(1, Math.round(s.qty * multiplier)) : s.qty,
    }));
    setItems(newItems);
    toast.success(`${eventType.label} package loaded!`);
  };

  const addFromTemplate = (template) => {
    const newItems = template.items.map((item, i) => ({
      id: `pkg-${Date.now()}-${i}`,
      ...item,
      qty: item.qty,
    }));
    setItems(newItems);
    toast.success(`${template.name} loaded!`);
  };

  const addProduct = (product) => {
    setItems(prev => [...prev, {
      id: `pkg-${Date.now()}`,
      garment: product.garment || "",
      method: product.method || "",
      type: product.type || "",
      label: product.label,
      qty: product.type === "stickers" ? 100 : product.type === "banner" ? 1 : 24,
      price: product.price,
    }]);
    setShowAdd(false);
  };

  const updateQty = (id, delta) => {
    setItems(prev => prev.map(item =>
      item.id === id ? { ...item, qty: Math.max(1, item.qty + delta) } : item
    ));
  };

  const removeItem = (id) => {
    setItems(prev => prev.filter(item => item.id !== id));
  };

  const getEstimate = () => {
    return items.reduce((sum, item) => sum + (item.price || 20) * item.qty, 0);
  };

  const addAllToCart = () => {
    if (items.length === 0) { toast.error("Add at least one product"); return; }
    const cart = JSON.parse(localStorage.getItem("cart") || "[]");
    items.forEach(item => {
      const gInfo = GARMENT_TYPES[item.garment] || { name: item.label, price: item.price || 20 };
      cart.push({
        id: `event-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        product_id: item.method || item.type || "dtf_printing",
        product_name: item.label,
        quantity: item.qty,
        price: (item.price || gInfo.price) * item.qty,
        unit_price: item.price || gInfo.price,
        totalPrice: (item.price || gInfo.price) * item.qty,
        garment_type: item.garment || "",
        garment_color: "White",
        size: "M",
        placement: "front",
      });
    });
    localStorage.setItem("cart", JSON.stringify(cart));
    toast.success(`${items.length} products added to cart!`);
    navigate("/cart");
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-8" data-testid="event-packages">
      <SEO title="Event Packages" description="Build multi-product orders for events" path="/event-packages" />

      <div className="text-center mb-8">
        <span className="font-mono text-[11px] uppercase tracking-[0.12em] text-muted">Multi-Product Orders</span>
        <h1 className="font-archivo text-4xl sm:text-5xl uppercase tracking-tight text-ink mb-2">Event Packages</h1>
        <p className="font-inter text-sm text-muted">Tell us your event — we'll suggest the perfect product mix</p>
      </div>

      {/* Event Planner */}
      <Card className="border-0 shadow-md mb-6 bg-gradient-to-r from-orange-50 to-amber-50" data-testid="event-planner">
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-4 h-4 text-orange-600" />
            <h2 className="text-sm font-bold text-gray-900">Event Planner</h2>
            <Badge className="bg-orange-100 text-orange-700 text-[9px]">Smart Suggest</Badge>
          </div>
          <div className="flex gap-2 mb-3">
            <Input
              type="number"
              value={attendees}
              onChange={(e) => setAttendees(e.target.value)}
              placeholder="How many attendees? (optional)"
              className="max-w-[240px] h-9 text-sm bg-white"
              data-testid="attendee-count"
            />
            <span className="text-xs text-gray-500 self-center">We'll scale quantities automatically</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {EVENT_TYPES.map(evt => (
              <button
                key={evt.id}
                onClick={() => loadFromEvent(evt)}
                className={`p-2.5 rounded-lg border text-center transition-all ${
                  selectedEvent === evt.id
                    ? "border-orange-500 bg-white shadow-sm ring-1 ring-orange-200"
                    : "border-gray-200 bg-white hover:border-orange-300"
                }`}
                data-testid={`event-${evt.id}`}
              >
                <evt.icon className={`w-5 h-5 mx-auto mb-1 ${selectedEvent === evt.id ? "text-orange-600" : "text-gray-400"}`} />
                <span className={`block text-[11px] font-semibold ${selectedEvent === evt.id ? "text-orange-700" : "text-gray-700"}`}>{evt.label}</span>
                <span className="block text-[9px] text-gray-400">{evt.suggestions.length} products</span>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Quick Templates */}
      <div className="mb-6">
        <h2 className="text-sm font-bold text-gray-800 mb-3">Quick Start Templates</h2>
        <div className="grid sm:grid-cols-3 gap-3">
          {PACKAGE_TEMPLATES.map(t => (
            <Card key={t.id} className="border-0 shadow-sm hover:shadow-md transition cursor-pointer" onClick={() => addFromTemplate(t)} data-testid={`template-${t.id}`}>
              <CardContent className="p-4 text-center">
                <Package className="w-8 h-8 text-orange-500 mx-auto mb-2" />
                <h3 className="text-sm font-bold text-gray-900">{t.name}</h3>
                <p className="text-xs text-gray-500">{t.desc}</p>
                <div className="flex flex-wrap justify-center gap-1 mt-2">
                  {t.items.map((item, i) => (
                    <Badge key={i} variant="secondary" className="text-[9px]">{item.label} x{item.qty}</Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Package Builder */}
      <Card className="border-0 shadow-md mb-4">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-bold text-gray-800">Your Package</h2>
            <Button size="sm" variant="outline" onClick={() => setShowAdd(!showAdd)} className="text-xs h-7" data-testid="add-product-btn">
              <Plus className="w-3.5 h-3.5 mr-1" /> Add Product
            </Button>
          </div>

          {showAdd && (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-4 p-3 bg-gray-50 rounded-lg" data-testid="product-picker">
              {PRODUCT_OPTIONS.map(p => (
                <button key={p.id} onClick={() => addProduct(p)}
                  className="p-2 rounded-lg border border-gray-200 hover:border-orange-400 text-left transition bg-white"
                  data-testid={`pick-${p.id}`}>
                  <p.icon className="w-4 h-4 text-orange-500 mb-1" />
                  <p className="text-[11px] font-semibold text-gray-800">{p.label}</p>
                  <p className="text-[10px] text-gray-400">~${p.price}/ea</p>
                </button>
              ))}
            </div>
          )}

          {items.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              <Package className="w-10 h-10 mx-auto mb-2 opacity-40" />
              <p className="text-sm">Pick an event type above or add products manually</p>
            </div>
          ) : (
            <div className="space-y-2">
              {items.map((item) => (
                <div key={item.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg" data-testid={`pkg-item-${item.id}`}>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-gray-900">{item.label}</p>
                    <p className="text-[10px] text-gray-400">
                      ~${((item.price || 20) * item.qty).toFixed(2)} total
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex items-center border rounded-md h-7 bg-white">
                      <button className="px-1.5 hover:bg-gray-100 h-full" onClick={() => updateQty(item.id, -1)}><Minus className="w-3 h-3" /></button>
                      <span className="w-10 text-center text-xs font-medium">{item.qty}</span>
                      <button className="px-1.5 hover:bg-gray-100 h-full" onClick={() => updateQty(item.id, 1)}><Plus className="w-3 h-3" /></button>
                    </div>
                    <Button size="sm" variant="ghost" onClick={() => removeItem(item.id)} className="text-red-400 hover:text-red-600 h-7 w-7 p-0">
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Summary + Actions */}
      {items.length > 0 && (
        <Card className="border-0 shadow-md bg-gradient-to-r from-orange-50 to-red-50" data-testid="package-summary">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-xs text-gray-500">{items.length} product{items.length !== 1 ? "s" : ""} &middot; {items.reduce((s, i) => s + i.qty, 0)} total items</p>
                <p className="text-2xl font-bold text-gray-900" data-testid="package-total">~${getEstimate().toFixed(2)}</p>
                <p className="text-[10px] text-gray-500">Exact pricing calculated at checkout</p>
              </div>
              <Button onClick={addAllToCart} className="bg-gradient-to-r from-orange-500 to-red-600 text-white font-bold h-11 px-6" data-testid="checkout-package-btn">
                <ShoppingCart className="w-4 h-4 mr-2" /> Add All to Cart
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default EventPackages;
